// PropertiesPanel.tsx
// Extracted from ProcessFlowPage to reduce CC (AIAG-VDA CC ≤ 15)
// Each named sub-component handles one case (node / edge / empty) — each CC ≤ 5
import React from 'react';
import clsx from 'clsx';
import { Trash2, Cog, Link2, Settings, Save, Plus } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import {
    ProcessFlowNode as ProcessFlowNodeType,
    ProcessFlowEdge as ProcessFlowEdgeType,
    ProcessStepType,
    ProcessStepData,
    SpecialCharacteristic,
    ProcessElement4M,
    ConnectionType,
} from '../../types/processFlow';
import { WorkElement } from '../../types';

// ── Props ─────────────────────────────────────────────────────────────────────
interface PropertiesPanelProps {
    selectedNode: ProcessFlowNodeType | null;
    selectedEdge: ProcessFlowEdgeType | null;
    onUpdateNode: (id: string, data: ProcessStepData) => void;
    onUpdateEdge: (id: string, data: any) => void;
    onDeleteNode: (id: string) => void;
    onDeleteEdge: (id: string) => void;
    workElements: WorkElement[];
    onAddWorkElement?: (stepId: string) => void;
    onUpdateWorkElement?: (elementId: string, updates: Partial<WorkElement>) => void;
    onDeleteWorkElement?: (elementId: string) => void;
    onSave: () => void;
    isSaving: boolean;
    canEdit: boolean;
}

// ── shared shell ──────────────────────────────────────────────────────────────
function PanelShell({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
    return (
        <aside className={clsx('w-72 border-l p-4 overflow-y-auto h-full shrink-0', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            {children}
        </aside>
    );
}

function FieldInput({ label, value, onChange, placeholder, isDark }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string; isDark: boolean;
}) {
    return (
        <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                className={clsx('w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none',
                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900')} />
        </div>
    );
}

// ── empty state ───────────────────────────────────────────────────────────────
function EmptyPanel({ isDark }: { isDark: boolean }) {
    return (
        <aside className={clsx('w-80 border-l p-4 flex flex-col h-full shrink-0', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
            <h3 className={clsx('font-semibold text-lg mb-4 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                <Settings size={18} className="text-slate-400" /> Properties
            </h3>
            <div className="flex flex-col items-center justify-center h-64 text-center opacity-60">
                <Settings className="text-slate-300 dark:text-slate-600 mb-3" size={48} />
                <p className={clsx('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                    Select a process step or connection to edit its properties
                </p>
            </div>
        </aside>
    );
}

// ── work elements sub-panel ───────────────────────────────────────────────────
function WorkElementsSection({ nodeId, workElements, isDark, onAdd, onUpdate, onDelete }: {
    nodeId: string;
    workElements: WorkElement[];
    isDark: boolean;
    onAdd: (id: string) => void;
    onUpdate: (id: string, updates: Partial<WorkElement>) => void;
    onDelete: (id: string) => void;
}) {
    const items = workElements.filter(we => we.processStepId === nodeId);
    return (
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Work Elements (4M)</label>
                <button onClick={() => onAdd(nodeId)} className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium">
                    <Plus size={12} /> Add
                </button>
            </div>
            <div className="space-y-2">
                {items.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No work elements defined.</p>
                ) : (
                    items.map(el => (
                        <div key={el.id} className={clsx('p-2 rounded border', isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200')}>
                            <div className="flex gap-2 mb-1">
                                <input type="text" value={el.name}
                                    onChange={e => onUpdate(el.id, { name: e.target.value })}
                                    placeholder="Element Name"
                                    className={clsx('flex-1 text-xs px-1.5 py-1 rounded border-transparent focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none', isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900')} />
                                <button onClick={() => onDelete(el.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={12} /></button>
                            </div>
                            <input type="text" value={el.description}
                                onChange={e => onUpdate(el.id, { description: e.target.value })}
                                placeholder="Description..."
                                className={clsx('w-full text-xs px-1.5 py-1 rounded border-transparent focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none opacity-80', isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900')} />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

// ── node form fields (selects) ────────────────────────────────────────────────
function NodeSelects({ nodeId, data, inputCls, onUpdate }: {
    nodeId: string;
    data: ProcessStepData;
    inputCls: string;
    onUpdate: (id: string, data: ProcessStepData) => void;
}) {
    return (
        <>
            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Step Type</label>
                <select value={data.type} onChange={e => onUpdate(nodeId, { ...data, type: e.target.value as ProcessStepType })} className={inputCls}>
                    <option value={ProcessStepType.OPERATION}>⚙️ Operation</option>
                    <option value={ProcessStepType.INSPECTION}>🔍 Inspection</option>
                    <option value={ProcessStepType.TRANSPORT}>➡️ Transport</option>
                    <option value={ProcessStepType.STORAGE}>📦 Storage</option>
                    <option value={ProcessStepType.DELAY}>⏳ Delay</option>
                    <option value={ProcessStepType.DECISION}>❓ Decision</option>
                    <option value={ProcessStepType.START_END}>⭕ Start/End</option>
                    <option value={ProcessStepType.SUBPROCESS}>📋 Subprocess</option>
                    <option value={ProcessStepType.SPC_CONTROL}>📊 SPC Control</option>
                    <option value={ProcessStepType.POKA_YOKE}>🛡️ Poka-Yoke</option>
                </select>
            </div>
            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Primary 4M Category</label>
                <select value={data.element4M || ''} onChange={e => onUpdate(nodeId, { ...data, element4M: e.target.value as ProcessElement4M || undefined })} className={inputCls}>
                    <option value="">-- Select --</option>
                    <option value={ProcessElement4M.MAN}>👤 Man (Operator)</option>
                    <option value={ProcessElement4M.MACHINE}>⚙️ Machine (Equipment)</option>
                    <option value={ProcessElement4M.MATERIAL}>📦 Material</option>
                    <option value={ProcessElement4M.METHOD}>📋 Method (Process)</option>
                </select>
            </div>
            <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Special Characteristic</label>
                <select value={data.specialCharacteristic || SpecialCharacteristic.NONE} onChange={e => onUpdate(nodeId, { ...data, specialCharacteristic: e.target.value as SpecialCharacteristic })} className={inputCls}>
                    <option value={SpecialCharacteristic.NONE}>None</option>
                    <option value={SpecialCharacteristic.CC}>🔴 CC (Critical)</option>
                    <option value={SpecialCharacteristic.SC}>🟡 SC (Significant)</option>
                    <option value={SpecialCharacteristic.STANDARD}>🔵 Standard</option>
                </select>
            </div>
        </>
    );
}

// ── node action buttons ───────────────────────────────────────────────────────
function NodeActions({ nodeId, onDelete, onSave, isSaving, canEdit }: {
    nodeId: string; onDelete: (id: string) => void;
    onSave: () => void; isSaving: boolean; canEdit: boolean;
}) {
    return (
        <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
            <button onClick={() => onDelete(nodeId)} className="w-full flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-800 rounded px-3 py-2 text-sm transition-colors">
                <Trash2 size={16} /> Delete Step
            </button>
            <button onClick={onSave} disabled={isSaving || !canEdit}
                className={clsx('w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium transition-all shadow-lg text-sm', canEdit ? 'bg-primary-500 hover:bg-primary-600 text-white' : 'bg-slate-700 text-slate-500 cursor-not-allowed')}
                title={canEdit ? 'Save diagram and submit to database' : 'You do not have permission to save'}>
                <Save size={16} /> {isSaving ? 'Saving...' : 'Submit to Database'}
            </button>
        </div>
    );
}

// ── node panel ────────────────────────────────────────────────────────────────
function NodePanel({ node, isDark, workElements, onUpdate, onDelete, onSave, isSaving, canEdit, onAddWE, onUpdateWE, onDeleteWE }: {
    node: ProcessFlowNodeType;
    isDark: boolean;
    workElements: WorkElement[];
    onUpdate: (id: string, data: ProcessStepData) => void;
    onDelete: (id: string) => void;
    onSave: () => void;
    isSaving: boolean;
    canEdit: boolean;
    onAddWE?: (stepId: string) => void;
    onUpdateWE?: (id: string, u: Partial<WorkElement>) => void;
    onDeleteWE?: (id: string) => void;
}) {
    const data = node.data;
    const inputCls = clsx('w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none', isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900');

    return (
        <PanelShell isDark={isDark}>
            <h3 className={clsx('font-semibold text-lg mb-4 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                <Cog size={18} className="text-primary-500" /> Step Properties
            </h3>
            <div className="space-y-4">
                <FieldInput label="Step Number" value={data.stepNumber || ''} onChange={v => onUpdate(node.id, { ...data, stepNumber: v })} placeholder="OP-010" isDark={isDark} />
                <FieldInput label="Label" value={data.label} onChange={v => onUpdate(node.id, { ...data, label: v })} isDark={isDark} />
                <NodeSelects nodeId={node.id} data={data} inputCls={inputCls} onUpdate={onUpdate} />
                {onAddWE && onUpdateWE && onDeleteWE && (
                    <WorkElementsSection nodeId={node.id} workElements={workElements} isDark={isDark} onAdd={onAddWE} onUpdate={onUpdateWE} onDelete={onDeleteWE} />
                )}
                <FieldInput label="Equipment/Station" value={data.equipment || ''} onChange={v => onUpdate(node.id, { ...data, equipment: v })} placeholder="CNC Lathe #1" isDark={isDark} />
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Cycle Time (seconds)</label>
                    <input type="number" value={data.cycleTime || ''} onChange={e => onUpdate(node.id, { ...data, cycleTime: parseInt(e.target.value) || undefined })} placeholder="120" className={inputCls} />
                </div>
                <FieldInput label="Control Method" value={data.controlMethod || ''} onChange={v => onUpdate(node.id, { ...data, controlMethod: v })} placeholder="SPC, 100% inspection, etc." isDark={isDark} />
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Description</label>
                    <textarea value={data.description || ''} onChange={e => onUpdate(node.id, { ...data, description: e.target.value })} rows={3}
                        className={clsx('w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none', isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900')} />
                </div>
                <NodeActions nodeId={node.id} onDelete={onDelete} onSave={onSave} isSaving={isSaving} canEdit={canEdit} />
            </div>
        </PanelShell>
    );
}


// ── edge panel ────────────────────────────────────────────────────────────────
function EdgePanel({ edge, isDark, onUpdate, onDelete }: {
    edge: ProcessFlowEdgeType;
    isDark: boolean;
    onUpdate: (id: string, data: any) => void;
    onDelete: (id: string) => void;
}) {
    const data = edge.data || {};
    const typeColors: Record<string, string> = {
        [ConnectionType.DEFAULT]: 'bg-slate-500',
        [ConnectionType.MATERIAL_FLOW]: 'bg-blue-500',
        [ConnectionType.REWORK]: 'bg-amber-500',
        [ConnectionType.REJECT]: 'bg-red-500',
        [ConnectionType.ALTERNATE]: 'bg-green-500',
        [ConnectionType.INFORMATION]: 'bg-purple-500',
    };

    return (
        <PanelShell isDark={isDark}>
            <h3 className={clsx('font-semibold text-lg mb-4 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                <Link2 size={18} className="text-primary-500" /> Connection Properties
            </h3>
            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Connection Type</label>
                    <div className="space-y-2">
                        {Object.values(ConnectionType).map(type => (
                            <button key={type} onClick={() => onUpdate(edge.id, { ...data, connectionType: type })}
                                className={clsx('w-full flex items-center gap-3 p-3 rounded-lg border text-sm transition-all',
                                    data.connectionType === type
                                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                                        : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700')}>
                                <div className={clsx('w-8 h-0.5', typeColors[type] || 'bg-slate-400')} />
                                <span className={isDark ? 'text-white' : 'text-slate-700'}>
                                    {type.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Label</label>
                    <input type="text" value={data.label || ''} onChange={e => onUpdate(edge.id, { ...data, label: e.target.value })} placeholder="Pass, Fail, etc."
                        className={clsx('w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 outline-none', isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900')} />
                </div>
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <button onClick={() => onDelete(edge.id)} className="w-full flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-800 rounded px-3 py-2 text-sm transition-colors">
                        <Trash2 size={16} /> Delete Connection
                    </button>
                </div>
            </div>
        </PanelShell>
    );
}

// ── main export (CC ≤ 5: just selects which sub-component to render) ──────────
const PropertiesPanel: React.FC<PropertiesPanelProps> = (props) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    if (!props.selectedNode && !props.selectedEdge) return <EmptyPanel isDark={isDark} />;
    if (props.selectedNode) {
        return <NodePanel node={props.selectedNode} isDark={isDark} workElements={props.workElements}
            onUpdate={props.onUpdateNode} onDelete={props.onDeleteNode} onSave={props.onSave}
            isSaving={props.isSaving} canEdit={props.canEdit}
            onAddWE={props.onAddWorkElement} onUpdateWE={props.onUpdateWorkElement} onDeleteWE={props.onDeleteWorkElement} />;
    }
    if (props.selectedEdge) {
        return <EdgePanel edge={props.selectedEdge} isDark={isDark} onUpdate={props.onUpdateEdge} onDelete={props.onDeleteEdge} />;
    }
    return null;
};

export default PropertiesPanel;
